"use client";

import { memo, useState, useEffect, useCallback, useRef } from 'react';
import styles from './MobileFilterModal.module.scss';
import { useFilter } from '@context/FilterContext';
import Text from '@components/atoms/Text';
import { Modal } from '@components/atoms/Modal';
import { cn } from '@utils/utils';
import DualRangeSlider from '@components/atoms/DualRangeSlider';
import ToggleSwitch from '@components/atoms/ToggleSwitch';
import { MobileFilterModalProps, FilterState, FilterCategory } from '@interfaces/filter.types';
import { filterHelpers } from '@helpers/filterLogic';
import { Button } from '@components/atoms/Button';
import { usePathname } from 'next/navigation';
import { createMobileFilterCategories } from '@helpers/filterConfigHelpers';
import SharedFilterOption from '@components/atoms/SharedFilterOption';
import { formatPrice } from '@utils/numberFormatter';
import { EventType, trackEventWithCommonProps } from '@utils/eventTracker';

const MobileFilterModal = memo(({ 
  filters, 
  isOpen, 
  filterType, 
  onApply, 
  onClose, 
  onClear, 
  onBudgetChange,
  onExpressDeliveryChange,
  hasActiveFilters,
  pageName,
  cityName,
  collectionName,
}: MobileFilterModalProps) => {
  const pathname = usePathname();
  const { state, dispatch, filterOptions } = useFilter();
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const [tabContentHeight, setTabContentHeight] = useState<number>(0);
  const isInitialOpen = useRef(true);

  const filterMetrics = filterHelpers.getFilterMetrics(state, filterOptions);

  const isExpressDeliveryCollection = filterHelpers.isExpressDeliveryCollection(pathname || "");

  const dynamicFilterCategories = createMobileFilterCategories(filterOptions, !isExpressDeliveryCollection);
  const hasFilterCategories = dynamicFilterCategories && dynamicFilterCategories.length > 1; // More than just Express Delivery
  const hasOnlyExpressDelivery = dynamicFilterCategories && dynamicFilterCategories.length === 1 && dynamicFilterCategories[0][0] === 'is_express_delivery';

  useEffect(() => {
    if (isOpen && isInitialOpen.current) {
      if (filterType) {
        setSelectedCategory(filterType);
      } else if (dynamicFilterCategories.length > 0) {
        setSelectedCategory(dynamicFilterCategories[0][0] as string);
      }
      isInitialOpen.current = false;
    }
    
    if (!isOpen) {
      isInitialOpen.current = true;
    }
  }, [isOpen, filterType, dynamicFilterCategories]);

  useEffect(() => {
    const calculateHeight = () => {
      const tabsElement = document.querySelector(`.${styles.specs__tabs}`);
      if (tabsElement && isOpen) {
        const tabsHeight = tabsElement.clientHeight;
        setTabContentHeight(tabsHeight);
      }
    };

    if (isOpen) {
      const timeoutId = setTimeout(calculateHeight, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, selectedCategory, dynamicFilterCategories.length]);

  const getCurrentFilterOptions = useCallback(() => {
    if (!filterOptions || !selectedCategory) return [];

    const value = (filterOptions as any)[selectedCategory];
    
    if (!value) return [];

    switch (selectedCategory) {
      case 'brand_model':
        return Array.isArray(value) ? value.map((brand: any) => ({
          label: brand.brand_name,
          value: brand.brand_name,
          subcategories: brand.models?.map((model: any) => ({
            label: model.model_name,
            value: model.model_name
          })) || []
        })) : [];
        
      case 'body_type':
        return Array.isArray(value) ? value.map((item: any) => ({
          label: item.key,
          value: item.value,
          subcategories: (item.sub_values || item.sub_segments)?.map((sub: any) => ({
            label: sub.key,
            value: sub.value
          })) || []
        })) : [];
    }
    
    if (Array.isArray(value)) {
      return value.map((item: any) => {
        if (item && typeof item === 'object' && 'key' in item && 'value' in item) {
          return {
            label: item.key,
            value: item.value,
            subcategories: item.sub_segments?.map((subItem: any) => ({
              label: subItem.key,
              value: subItem.value
            })) || []
          };
        }
        
        return {
          label: item.label || item.name || item.toString(),
          value: item.value || item.key || item.toString()
        };
      });
    }
    
    return [];
  }, [filterOptions, selectedCategory]);

  const currentFilterOptions = getCurrentFilterOptions();

  const handleOptionSelect = useCallback(async (optionValue: string) => {
    const categoryState = state[selectedCategory as keyof FilterState];
    const currentValues = Array.isArray(categoryState) ? categoryState : [];
    // await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
    //   pageType: pageName,
    //   additionalProps: {
    //     action_type: "tap_btn",
    //     action_name: "tap_btn_filter_apply",
    //     selection: Array.isArray(currentValues) ? [...currentValues, optionValue].join(",") : optionValue,
    //     city_name: cityName,
    //     collection_name: collectionName,
    //   },
    // });
    const newValues = filterHelpers.handleHierarchicalFilterSelection(
      currentValues, 
      optionValue, 
      filterOptions,
      selectedCategory as FilterCategory
    );
    
    dispatch({ type: 'SET_FILTER', category: selectedCategory as any, values: newValues });
  }, [selectedCategory, state, dispatch, filterOptions]);

  const handleExpressDeliveryToggle = useCallback(() => {
    const newValue = !state.is_express_delivery;
    onExpressDeliveryChange(newValue);
  }, [state.is_express_delivery, onExpressDeliveryChange]);

  const handleSortSelect = useCallback((sortValue: string) => {
    const currentValues = state.sort_by || [];
    const allowMultiSelect = false;
    
    const newValues = filterHelpers.handleFilterSelection(
      currentValues, 
      sortValue, 
      allowMultiSelect
    );
    
    dispatch({ type: 'SET_FILTER', category: 'sort_by', values: newValues });
  }, [state.sort_by, dispatch]);

  const handleTabClick = useCallback((key: string) => {
    setSelectedCategory(key);
  }, []);

  const renderExpressDeliveryToggle = useCallback(() => (
    <div className={styles.toggleContainer}>
      <Text className={styles.toggleLabel}>Express Delivery</Text>
      <ToggleSwitch
        isOn={state.is_express_delivery}
        onToggle={handleExpressDeliveryToggle}
      />
    </div>
  ), [state.is_express_delivery, handleExpressDeliveryToggle]);

  const renderSortOptions = useCallback(() => {
    const sortOptions = filterOptions?.sort_by || [];
    const currentValues = state.sort_by || [];

    if (sortOptions.length === 0) {
      return <div className={styles.noData}>No sort options available</div>;
    }

    return (
      <div className={styles.options}>
        {sortOptions.map((option: any) => (
          <SharedFilterOption
            key={option.value}
            option={{
              label: option.key,
              value: option.value,
              subcategories: []
            }}
            selectedValues={currentValues}
            onSelect={handleSortSelect}
            showSubcategories={false}
          />
        ))}
      </div>
    );
  }, [filterOptions?.sort_by, state.sort_by, handleSortSelect]);

  const renderBudgetOptions = useCallback(() => (
    <div className={styles.budgetContainer}>
      <DualRangeSlider
        minValue={state.budget.min || filterMetrics.budgetDefaults.min}
        maxValue={state.budget.max || filterMetrics.budgetDefaults.max}
        min={filterMetrics.budgetDefaults.min}
        max={filterMetrics.budgetDefaults.max}
        step={100000}
        onChange={onBudgetChange}
      />
      <div className={styles.valueDisplay}>
        <div className={styles.priceBox}>{formatPrice(state.budget.min)}</div>
        <div className={styles.priceBox}>{formatPrice(state.budget.max)}</div>
      </div>
    </div>
  ), [state.budget, filterMetrics.budgetDefaults, onBudgetChange]);

  const renderRegularOptions = useCallback(() => {
    if (!currentFilterOptions || currentFilterOptions.length === 0) {
      return <div className={styles.noData}>No {selectedCategory} options available</div>;
    }
    
    const categoryState = state[selectedCategory as keyof FilterState];
    const currentValues = Array.isArray(categoryState) ? categoryState : [];
    
    return (
      <div className={styles.options}>
        {currentFilterOptions.map((option: any, index: any) => (
          <SharedFilterOption
            key={`${option.value}-${index}`}
            option={option}
            selectedValues={currentValues}
            onSelect={handleOptionSelect}
            showSubcategories={true}
          />
        ))}
      </div>
    );
  }, [currentFilterOptions, selectedCategory, state, handleOptionSelect]);

  const renderSelectedFilterContent = useCallback(() => {
    switch (selectedCategory) {
      case 'is_express_delivery':
        return renderExpressDeliveryToggle();
      case 'sort_by':
        return renderSortOptions();
      case 'budget':
        return renderBudgetOptions();
      default:
        return renderRegularOptions();
    }
  }, [
    selectedCategory, 
    renderExpressDeliveryToggle, 
    renderSortOptions, 
    renderBudgetOptions, 
    renderRegularOptions
  ]);

  if (dynamicFilterCategories.length === 0 && !hasActiveFilters) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      overlayClassName={styles.modalOverlay}
      contentClassName={styles.modalContent}
      headerClassName={styles.modalHeader}
      title="Filter"
    >
      <div 
        className={styles.specs__content}
        style={{
          height: `${tabContentHeight}px`,
        }}
      >
        {hasFilterCategories ? (
          <>
            <div 
              className={styles.specs__tabs} 
              id='specs__tabs'
            >
              {dynamicFilterCategories.map(([key, category]: any, index: number) => {
                const selectionCount = filterMetrics.counts[key as keyof typeof filterMetrics.counts] || 0;

                return (
                  <Button
                    key={`${key}-${index}`}
                    className={cn(
                      styles.specs__tab,
                      selectedCategory === key && styles.specs__tab_active
                    )}
                    onClick={() => handleTabClick(key as string)}
                  >
                    {filterHelpers.getFilterTitleWithCount(category.title, selectionCount)}
                  </Button>
                );
              })}
            </div>

            <div className={styles.specs__tabContent}>
              {renderSelectedFilterContent()}
            </div>
          </>
        ) : hasOnlyExpressDelivery ? (
          <div className={styles.specs__tabContent}>
            {renderSelectedFilterContent()}
          </div>
        ) : (
          <div className={styles.specs__tabContent}>
            <div className={styles.noFiltersMessage}>
              <Text className={styles.noFiltersText}>
                No additional filters available for this selection.
              </Text>
            </div>
          </div>
        )}
      </div>
      
      <div className={styles.bottomButtons}>
        <Button 
          onClick={onClear} 
          className={styles.clearModalButton}
          disabled={!hasActiveFilters}
        >
          Clear all
        </Button>
        <Button 
          onClick={onApply} 
          className={styles.applyModalButton}
          disabled={!hasActiveFilters}
        >
          Apply
        </Button>
      </div>
    </Modal>
  );
});

MobileFilterModal.displayName = 'MobileFilterModal';

export default MobileFilterModal; 